import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import RenderField from "./subComponents/RenderField";
import { useCreateProject } from "../../../../../queries/query-hooks/workspace/workspace-hook";
import * as Yup from "yup";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { getPrincipleEntitiesForSelect } from "../../../../../common/utils/utils";
import postService from "../../../../../api/services/post-service";
import {
  ADD_PROJECT,
  GET_BANNER_GROUP,
  GET_CTA_MASTER,
  GET_SEARCH_CONTENT,
  UPDATE_PROJECT,
} from "../../../../../api/endpoints/channel-endpoint";
import toast from "react-hot-toast";
import useBackdrop from "../../../../../components/custom-hooks/use-backdrop";
import { useQueryClient } from "@tanstack/react-query";
import { GET_PROJECT_POST_LIST } from "../../../../../queries/query-constants/workspace/workspace-constant";
import useRedirect from "../../../../../components/custom-hooks/use-redirect";
import debounce from "lodash/debounce";
import FilePicker from "../../../../../components/form-components/file-picker";
import uploadService from "../../../../../api/services/upload-service";
import { RESOURCES_FILE_UPLOAD } from "../../../../../api/endpoints/help-and-support-endpoints";
import { findIcon } from "../../../../dashboard/icons";
import EmslRemainder from "./subComponents/EmslRemainder";
import FormBuilder from "./subComponents/FormBuilder";

const EmslForm = ({
  project_type_code,
  project_group_id,
  project_type_id,
  projectDetails = [],
  channelData = [],
}) => {
  const { showBackdrop, hideBackdrop } = useBackdrop();
  const { redirectTo } = useRedirect();
  const queryClient = useQueryClient();
  const { data: createProjectData, isLoading: principalEntitiesLoading } =
    useCreateProject();
  const [searchText, setSearchText] = useState({});
  const [typeOfSearch, setTypeOfSearch] = useState("campaign");
  const [selectedTaskTypes, setSelectedTaskTypes] = useState(["on_call"]);
  const [lblDocuments, setLblDocuments] = useState([]);
  const [createdTags, setCreatedTags] = useState([]);
  const [isEditProject, setIsEditProject] = useState(false);
  const [remainders, setRemainders] = useState([
    {
      time: {
        day: "",
        hour: "",
        minute: "",
      },
      question_no: 0,
      spqId: "",
      template: {},
      channels: {
        sms: false,
        email: false,
        whatsapp: false,
      },
      surveyType: "",
    },
  ]);

  function timeToMinutes(time) {
    const days = parseInt(time.day || 0, 10);
    const hours = parseInt(time.hour || 0, 10);
    const minutes = parseInt(time.minute || 0, 10);
    return days * 24 * 60 + hours * 60 + minutes;
  }

  function minutesToTime(totalMinutes, isWithDays = false) {
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);

    if (isWithDays) {
      const days = Math.floor(totalHours / 24);
      const hours = totalHours % 24;
      return {
        day: String(days),
        hour: String(hours),
        minute: String(minutes),
      };
    } else {
      const hours = totalHours;
      return {
        hour: String(hours),
        minute: String(minutes),
      };
    }
  }

  function validateRemainders(remainders) {
    let isValid = true;

    remainders.forEach((item, index) => {
      if (item.surveyType && (!item.spqId || item.spqId.trim() === "")) {
        toast.error(`Survey ID is required since Survey Type is selected`);
        isValid = false;
      }

      if (item.surveyType && item.question_no === 0) {
        toast.error(
          `Question Number is required since Survey Type is selected`
        );
        isValid = false;
      }

      if (item.time <= 4) {
        toast.error(`Please choose reminder time`);
        isValid = false;
      }

      const channels = item.channels || {};
      const hasAtLeastOneChannel = Object.values(channels).some(Boolean);
      if (!hasAtLeastOneChannel) {
        toast.error(`Please choose reminder template`);
        isValid = false;
      }

      for (const key of Object.keys(channels)) {
        if (channels[key]) {
          if (!item.template || !item.template[key]) {
            toast.error(`Please choose reminder template`);
            isValid = false;
          }
        }
      }
    });

    return isValid;
  }

  const remainderData = remainders.map((item) => ({
    ...item,
    time: timeToMinutes(item.time),
  }));

  const formik = useFormik({
    initialValues: {
      project_name: "",
      project_desc: "",
      principal_entity_id: "",
      doctor_registration: 1,
      project_attributes: "",
      project_start_date: "",
      project_end_date: "",
      project_owner: "",
      project_contact_name: "",
      project_contact_email: "",
      project_contact_phone: "",
      project_type_id: project_type_id,
      project_group_id: project_group_id,
      project_status: 1,
      project_po_ref: "",
      project_po_date: "",
      project_logo: "",
      prescribers_target: "",
      lbl_thanks_template: "",
      lbl_feedback_template: "",
      project_to_content: [
        {
          label: "",
          content_id: "",
          content_category: "",
        },
      ],
    },
    validationSchema: Yup.object({
      project_name: Yup.string().trim().required("Project Name is required"),
      project_desc: Yup.string()
        .trim()
        .required("Project Description is required"),
      principal_entity_id: Yup.string().required(
        "Principal Entity is required"
      ),
      project_start_date: Yup.date()
        .required("Start date is required")
        .test(
          "start_date_before_end_date",
          "Start date must be before the End date.",
          function (value) {
            const { project_end_date } = this.parent;
            return (
              !project_end_date ||
              !value ||
              new Date(value) < new Date(project_end_date)
            );
          }
        ),
      project_end_date: Yup.date()
        .required("End date is required")
        .test(
          "end_date_after_start_date",
          "End date must be after the Start date.",
          function (value) {
            const { project_start_date } = this.parent;
            return (
              !project_start_date ||
              !value ||
              new Date(value) > new Date(project_start_date)
            );
          }
        ),
      project_owner: Yup.string()
        .trim()
        .required("Project Owner name is required"),
      project_contact_name: Yup.string()
        .trim()
        .required("Project contact person name is required"),
      project_contact_email: Yup.string()
        .trim()
        .required("Project contact person Email is required")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Please enter a valid email address."
        ),
      project_contact_phone: Yup.string()
        .trim()
        .matches(/^[6-9]\d{9}$/, {
          message: "Please enter valid number.",
          excludeEmptyString: false,
        })
        .required("Project contact person phone is required"),
      project_po_ref: Yup.string()
        .trim()
        .required("Project PO Reference is required"),
      project_po_date: Yup.string()
        .trim()
        .required("Project PO Date is required"),
      project_logo: Yup.string().required("Project Logo is required"),
      prescribers_target: Yup.number()
        .required("Project Impressions Target is required")
        .moreThan(0, "Please enter valid Impression target."),
      lbl_thanks_template: Yup.number()
        .required("Project LBL Thanks Template ID is required")
        .moreThan(0, "Please enter valid LBL thanks template ID."),
      lbl_feedback_template: Yup.number()
        .required("Project LBL Feedback Template ID is required")
        .moreThan(0, "Please enter valid LBL feedback template ID."),
    }),
    onSubmit: async (values) => {
      const payload = { ...values };
      payload.project_status === 0
        ? (payload.project_status = 2)
        : (payload.project_status = 1);

      payload.project_attributes = [
        {
          key: "doctor_registration",
          value: values.doctor_registration,
        },
        {
          key: "task_type",
          value: JSON.stringify(selectedTaskTypes),
        },
        {
          key: "lbl_document",
          value: JSON.stringify(lblDocuments),
        },
        {
          key: "tags",
          value: JSON.stringify(createdTags),
        },
        {
          key: "lbl_thanks_template",
          value: values.lbl_thanks_template,
        },
        {
          key: "lbl_feedback_template",
          value: values.lbl_feedback_template,
        },
        {
          key: "reminders",
          value: JSON.stringify(remainderData),
        },
      ];

      const projectToEmsl = payload.project_to_content?.map((item) => {
        return {
          type: item.content_category,
          type_id: item.content_id,
          type_label: item.label,
        };
      });

      payload.project_to_emsl = projectToEmsl || [];

      if (lblDocuments?.length < 1) {
        return toast.error("Please select at least one label document");
      }
      if (selectedTaskTypes?.length < 1) {
        return toast.error("Please select at least one task type");
      }
      if (
        !projectToEmsl?.length ||
        projectToEmsl.every(
          (item) =>
            !item.type?.trim() || !item.type_id || !item.type_label?.trim()
        )
      ) {
        return toast.error("Please select at least one content");
      }

      const isValidRemainders = validateRemainders(remainderData);
      if (!isValidRemainders) return;

      try {
        showBackdrop();
        delete payload?.dbRef;
        delete payload?.lbl_thanks_template;
        delete payload?.lbl_feedback_template;
        let response;
        if (projectDetails.length > 0) {
          response = await postService(UPDATE_PROJECT, payload);
        } else {
          response = await postService(ADD_PROJECT, payload);
        }

        if (response?.data?.success) {
          queryClient.invalidateQueries({
            queryKey: [GET_PROJECT_POST_LIST],
          });
          toast.success("Project created successfully");
          redirectTo("workspace/clirnet");
        } else {
          toast.error("Something went wrong");
        }
      } catch (e) {
        console.log(e);
        toast.error("Something went wrong");
      } finally {
        hideBackdrop();
      }
    },
  });

  const {
    values,
    touched,
    errors,
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
  } = formik;

  useEffect(() => {
    if (projectDetails.length > 0) {
      setIsEditProject(true);
      preFillProjectForm();
    }
  }, []);

  function preFillProjectForm() {
    const prescribersTarget =
      projectDetails?.at(0)?.project_target?.prescribers_target || 0;
    const projectStatus = projectDetails?.at(0)?.project_status === 2 ? 0 : 1;

    const projectToEmsl = projectDetails
      ?.at(0)
      ?.project_to_emsl?.map((item) => {
        return {
          label: item?.type_label,
          content_id: item?.type_id,
          content_category: item?.type,
        };
      });

    const projectSelectedTypes = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "task_type"
      )?.attribute_value;

    const projectSelectedLblDocuments = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "lbl_document"
      )?.attribute_value;

    const lblThanksTemplate = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "lbl_thanks_template"
      )?.attribute_value;

    const lblFeedbackTemplate = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "lbl_feedback_template"
      )?.attribute_value;

    const remainderData = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "reminders"
      )?.attribute_value;

    const formattedRemainderJSON = remainderData
      ? JSON.parse(remainderData)
      : [
          {
            time: {
              day: "",
              hour: "",
              minute: "",
            },
            question_no: 0,
            spqId: "",
            template: {},
            channels: {
              sms: false,
              email: false,
              whatsapp: false,
            },
            surveyType: "",
          },
        ];

    const formattedRemainderData = formattedRemainderJSON?.map((item) => {
      return {
        ...item,
        time: minutesToTime(item.time, true),
      };
    });

    const projectSelectedTypesArray = projectSelectedTypes
      ? JSON.parse(projectSelectedTypes)
      : [];
    const projectSelectedLblDocumentsArray = projectSelectedLblDocuments
      ? JSON.parse(projectSelectedLblDocuments)
      : [];

    const projectTags = projectDetails
      ?.at(0)
      ?.project_attributes?.find(
        (item) => item?.attribute_name === "tags"
      )?.attribute_value;

    const projectTagsArray = projectTags ? JSON.parse(projectTags) : [];

    setSelectedTaskTypes(projectSelectedTypesArray);
    setLblDocuments(projectSelectedLblDocumentsArray);
    setCreatedTags(projectTagsArray);
    setRemainders(formattedRemainderData);

    setValues({
      ...values,
      ...projectDetails?.at(0),
      project_status: projectStatus,
      prescribers_target: prescribersTarget,
      lbl_thanks_template: lblThanksTemplate,
      lbl_feedback_template: lblFeedbackTemplate,
      project_to_content: projectToEmsl,
    });
  }

  const onTagsChangeHandler = (selectedValue) => {
    setCreatedTags(selectedValue);
  };

  const tabOptions = [
    {
      item: "on_call",
      tab: "On Call",
      disabled: true,
    },
    {
      item: "on_video",
      tab: "On Video",
      disabled: false,
    },
  ];

  const fields = [
    {
      row: 1,
      columns: [
        {
          id: 1,
          label: "Project Name",
          field_name: "project_name",
          type: "text",
          placeholder: "Enter Project Name",
          required: true,
          colSize: 6,
        },
        {
          id: 2,
          label: "Principal Entity",
          field_name: "principal_entity_id",
          type: "select",
          options: getPrincipleEntitiesForSelect(
            createProjectData?.principal_entities || []
          ),
          required: true,
          optionLabelKey: "principal_entity_name",
          optionValueKey: "principal_entity_id",
          placeholder: "Select Principal Entity",
          colSize: 3,
        },
        {
          id: 3,
          label: "Project Status",
          field_name: "project_status",
          type: "switch",
          required: false,
          disabled: !isEditProject,
          colSize: 3,
        },
      ],
    },
    {
      row: 2,
      columns: [
        {
          id: 1,
          label: "Project Description",
          field_name: "project_desc",
          type: "textarea",
          placeholder: "Enter Project Description",
          required: true,
        },
        {
          id: 2,
          label: "Project Logo",
          field_name: "project_logo",
          type: "file",
          required: true,
        },
      ],
    },
    {
      row: 3,
      columns: [
        {
          id: 1,
          label: "Project Owner Name",
          field_name: "project_owner",
          type: "text",
          placeholder: "Enter Project Owner",
          required: true,
        },
        {
          id: 2,
          label: "Project Start Date",
          field_name: "project_start_date",
          type: "date",
          required: true,
        },
        {
          id: 3,
          label: "Project End Date",
          field_name: "project_end_date",
          type: "date",
          required: true,
        },
      ],
    },
    {
      row: 4,
      columns: [
        {
          id: 1,
          label: "Project Contact Person Name",
          field_name: "project_contact_name",
          type: "text",
          placeholder: "Enter Owner Contact",
          required: true,
        },
        {
          id: 2,
          label: "Project Contant Person Email",
          field_name: "project_contact_email",
          type: "text",
          placeholder: "Enter Owner Email",
          required: true,
        },
        {
          id: 3,
          label: "Project Contact Person Phone",
          field_name: "project_contact_phone",
          type: "number",
          placeholder: "Enter Owner Phone",
          required: true,
        },
      ],
    },
    {
      row: 5,
      columns: [
        {
          id: 1,
          label: "Purchase Order Reference",
          field_name: "project_po_ref",
          type: "text",
          placeholder: "Enter Purchase Order Reference",
          required: true,
        },
        {
          id: 2,
          label: "Purchase Order Date",
          field_name: "project_po_date",
          type: "date",
          placeholder: "Enter Purchase Order Date",
          required: true,
        },
      ],
    },
    {
      row: 6,
      columns: [
        {
          id: 1,
          label: "Project Prescribers Target",
          field_name: "prescribers_target",
          placeholder: "Enter Project Prescribers Target",
          type: "number",
          required: true,
        },
        {
          id: 2,
          label: "LBL Thanks Template ID",
          field_name: "lbl_thanks_template",
          placeholder: "Enter LBL thanks template ID",
          type: "number",
          required: true,
        },
        {
          id: 3,
          label: "LBL Feedback Template ID",
          field_name: "lbl_feedback_template",
          placeholder: "Enter LBL feedback template ID",
          type: "number",
          required: true,
        },
      ],
    },
    {
      row: 7,
      columns: [
        {
          id: 1,
          label: "Show doctor registration number",
          field_name: "doctor_registration",
          type: "switch",
          required: false,
        },
        {
          id: 2,
          label: "Select Task Type",
          field_name: "task_type",
          type: "checkbox",
          required: true,
          options: tabOptions,
          selected: selectedTaskTypes,
          onCustomChange: (item) => {
            if (selectedTaskTypes?.includes(item)) {
              setSelectedTaskTypes(
                selectedTaskTypes?.filter((value) => value !== item)
              );
            } else {
              setSelectedTaskTypes((prev) => [...prev, item]);
            }
          },
        },
      ],
    },
    {
      row: 8,
      columns: [
        {
          id: 1,
          label: "Project Tags",
          field_name: "tags",
          type: "creatableSelect",
          required: false,
          options: createdTags,
          onCustomChange: onTagsChangeHandler,
        },
      ],
    },
  ];

  const getSearchAPIByType = (type) => {
    if (type === "campaign") return GET_BANNER_GROUP;
    if (type === "cta") return GET_CTA_MASTER;
    return GET_SEARCH_CONTENT;
  };

  const _loadSuggestions = (inputValue) => {
    const getTypeOfSearchAPI = getSearchAPIByType(typeOfSearch);
    return new Promise(async (resolve) => {
      const response = await postService(getTypeOfSearchAPI, {
        search_key: inputValue,
        search_type: typeOfSearch,
      });
      if (!response.isError) {
        const searchData = [];

        response?.data?.data.forEach((elem) => {
          searchData.push({
            value: elem.id,
            label: `${elem.id} - ${elem.title}`,
          });
        });
        resolve(searchData);
      } else {
        toast.error("Error fetching data!");
      }
    });
  };

  const getFileType = (url = "") => {
    if (typeof url !== "string") return "unknown";
    const extension = url.split(".").pop().toLowerCase();
    if (
      ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(extension)
    ) {
      return "image";
    }
    if (["pdf"].includes(extension)) {
      return "pdf";
    }
    if (["doc", "docx"].includes(extension)) {
      return "document";
    }
    if (["xls", "xlsx", "csv"].includes(extension)) {
      return "spreadsheet";
    }
    if (["mp4", "avi", "mov", "mkv", "webm"].includes(extension)) {
      return "video";
    }
    if (["mp3", "wav", "ogg", "aac"].includes(extension)) {
      return "audio";
    }
    return "unknown";
  };

  function uploadFiles(files) {
    showBackdrop();
    return new Promise(async (resolve, reject) => {
      if (files.length === 0) {
        reject("No files to upload.");
        hideBackdrop();
        return;
      }
      let formdata = new FormData();
      files.forEach((file) => {
        formdata.append("files[]", file);
      });
      let response = await uploadService(RESOURCES_FILE_UPLOAD, formdata);
      if (response.data?.success) {
        const url = response?.data?.data["0"]?.url;
        const id = response?.data?.data["0"]?.name;
        const filename = response?.data?.data?.filename;
        resolve({ url, id, filename });
        hideBackdrop();
      } else {
        reject("Something went wrong");
        hideBackdrop();
      }
    });
  }
  async function handleFileChange(e) {
    const { name, files } = e.target;
    const { url, filename, id } = await uploadFiles([...files]);
    const type = getFileType(url);
    setLblDocuments((prev) => [
      ...prev,
      {
        url: url,
        id: id,
        filename: filename,
        type: type,
      },
    ]);
  }
  const onDeleteLblDoc = (file) => {
    const updatedLblDocuments = lblDocuments.filter(
      (item) => item?.id !== file?.id
    );
    setLblDocuments(updatedLblDocuments);
  };

  const CONTENT_CATEGORY = [
    {
      content_category_name: "Campaign",
      content_category_code: "campaign",
    },
    {
      content_category_name: "CTA",
      content_category_code: "cta",
    },
    {
      content_category_name: "Session",
      content_category_code: "session",
    },
    {
      content_category_name: "Video",
      content_category_code: "video",
    },
    {
      content_category_name: "MedWiki",
      content_category_code: "medwiki",
    },
  ];

  return (
    <div>
      <Form onSubmit={handleSubmit}>
        <FormBuilder
          fields={fields}
          handleChange={handleChange}
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
        />

        <Row className="mb-3">
          <Col md={6} className="mb-3">
            <FilePicker
              title={
                <>
                  {values["lbl_documents"]
                    ? `Update LBL Documents`
                    : `Upload LBL Documents`}{" "}
                  {<span className="text-danger">*</span>}
                </>
              }
              onUpdate={(val) =>
                handleFileChange({
                  target: {
                    files: val,
                    name: "lbl_document",
                  },
                })
              }
              accepts="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              fileName={null}
              type={null}
              source={null}
            />
          </Col>
          <Col md={6}>
            <Row>
              {lblDocuments.map((file, index) => (
                <Col md={3} xs={6} key={index} className="position-relative">
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <Card className="p-2 mb-3 border shadow-none rounded">
                      {file?.type?.startsWith("image") ? (
                        <img
                          src={file.url}
                          alt={file.filename}
                          className="img-fluid rounded"
                          style={{ height: "50px", objectFit: "contain" }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center gap-2"
                          style={{ height: "50px", objectFit: "contain" }}
                        >
                          {file?.type === "pdf"
                            ? findIcon("pdf", "solid", 12)
                            : findIcon("doc", "solid", 12)}
                          <span className="text-truncate" title={file.filename}>
                            {file.filename}
                          </span>
                        </div>
                      )}
                    </Card>
                  </a>
                  <span
                    className="text-danger z-3 mt-1 me-3 cursor-pointer bg-white rounded-circle p-2 position-absolute top-0 end-0"
                    onClick={() => onDeleteLblDoc(file)}
                  >
                    {findIcon("Trash", "dual-tone", 22)}
                  </span>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>

        <Row className="align-items-md-center mb-4">
          <p className="form-label">Remainders</p>
          {remainders.map((item, index) => {
            return (
              <Col key={`remainder-${index}`} md={12}>
                <EmslRemainder
                  index={index}
                  remainders={remainders}
                  setRemainders={setRemainders}
                />
              </Col>
            );
          })}
        </Row>

        <div className="d-flex flex-column">
          <p className="form-label">Choose Contents</p>
          <FormikProvider value={formik}>
            <FieldArray name="project_to_content">
              {(prop) => (
                <>
                  {values.project_to_content?.map((item, index) => {
                    return (
                      <div
                        key={index}
                        className="border border-1 border-primary border-opacity-25 border-dashed rounded-3 mb-4 p-4 px-3"
                      >
                        <div className="row">
                          <Col lg={6}>
                            <RenderField
                              type="select"
                              field_name={`project_to_content[${index}].content_category`}
                              placeholder="Select Content Category"
                              values={values}
                              touched={touched}
                              errors={errors}
                              options={CONTENT_CATEGORY?.map((item) => {
                                return {
                                  label: item?.content_category_name,
                                  value: item?.content_category_code,
                                };
                              })}
                              handleChange={(e) => {
                                handleChange(e);
                                setTypeOfSearch(e.target.value);
                                setFieldValue(
                                  `project_to_content[${index}].content_id`,
                                  ""
                                );
                                setFieldValue(
                                  `project_to_content[${index}].label`,
                                  ""
                                );
                              }}
                              label={"Content Category"}
                              required={true}
                            />
                          </Col>

                          <Col lg={6}>
                            <RenderField
                              required={true}
                              type="searchable"
                              placeholder="Type your content to search"
                              label={`Select Content *`}
                              field_name={`project_to_content[${index}].content_id`}
                              searchText={searchText}
                              onFocusHandler={() =>
                                setTypeOfSearch(item?.content_category)
                              }
                              handleChange={(val) => {
                                if (val) {
                                  setFieldValue(
                                    `project_to_content[${index}].content_id`,
                                    val?.value || ""
                                  );
                                  setFieldValue(
                                    `project_to_content[${index}].label`,
                                    val?.label || ""
                                  );
                                } else {
                                  setFieldValue(
                                    `project_to_content[${index}].content_id`,
                                    ""
                                  );
                                  setFieldValue(
                                    `project_to_content[${index}].label`,
                                    ""
                                  );
                                }
                              }}
                              loadOptions={debounce((inputText, callback) => {
                                setSearchText(inputText);
                                _loadSuggestions(inputText)
                                  .then((options) => {
                                    callback(options);
                                  })
                                  .catch((err) => {
                                    console.log(err);
                                  });
                              }, 300)}
                              selected={
                                values.project_to_content[index]?.label
                                  ? {
                                      value:
                                        values.project_to_content[index]
                                          ?.content_id,
                                      label:
                                        values.project_to_content[index]?.label,
                                    }
                                  : ""
                              }
                              values={values}
                              touched={touched}
                              errors={errors}
                              setFieldValue={setFieldValue}
                              disabled={
                                values[`project_to_content`][index]
                                  ?.content_category == ""
                                  ? true
                                  : false
                              }
                            />
                          </Col>

                          <div className="col-12 d-flex justify-content-center align-items-center gap-2 flex-wrap">
                            <Button
                              variant="outline-danger"
                              className="border-dashed px-md-4 px-3"
                              onClick={() => prop?.remove(index)}
                              disabled={
                                index === 0 &&
                                values[`project_to_content`]?.length < 2
                                  ? true
                                  : false
                              }
                            >
                              <span>{findIcon("Trash", "dual-tone", 22)}</span>{" "}
                              Remove
                            </Button>

                            {values?.project_to_content?.length - 1 ===
                              index && (
                              <Button
                                variant={`${
                                  values[`project_to_content`][index]?.label ==
                                  ""
                                    ? "outline-secondary"
                                    : "outline-primary"
                                }`}
                                className="border-dashed px-md-4 px-3"
                                onClick={() => {
                                  prop?.push({
                                    label: "",
                                    content_id: "",
                                    content_category: "",
                                  });
                                }}
                                disabled={
                                  values[`project_to_content`][index]?.label ==
                                  ""
                                    ? true
                                    : false
                                }
                              >
                                <span>{findIcon("Plus", "solid", 22)}</span> Add
                                Row
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </FieldArray>
          </FormikProvider>
        </div>

        <div className="d-flex justify-content-end gap-3">
          <Button
            variant="secondary"
            onClick={() => redirectTo("workspace/clirnet")}
          >
            Cancel
          </Button>
          <Button type="submit" className="me-4">{`${
            projectDetails.length > 0 ? "Update" : "Submit"
          }`}</Button>
        </div>
      </Form>
    </div>
  );
};

export default EmslForm;
